/* eslint-disable no-nested-ternary */
import { useEffect, useState, useRef } from 'react';
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
} from 'react-virtualized';
import Swal from 'sweetalert2';
import { FaEye, FaSearch, FaEraser, FaExternalLinkAlt } from 'react-icons/fa';
import {
  Button,
  Card,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import api from 'services/api';
import { CircularProgress, TextField } from '@material-ui/core';
import UserSmallHeader from 'components/Headers/UserSmallHeader';
import Select from 'react-select';

interface AtendimentoItem {
  NOME_SOLICITANTE: string;
  SEQ_FLUXO_REAL: number;
  DATA_CRIACAO: string;
  SOLICITANTE: string;
  STATUS: string | null;
  RESPONSAVEL: string | null;
  PROCESSO: string | null;
  DATA_INICIO: string | null;
  DATA_FIM: string | null;
  SOLICITACAO: string;
  CONFIGURACAO: any;
  HIERARQUIA: any;
  PROPOSTA: any;
  URL: string;
}

interface GroupedOption {
  label: string;
  options: {
    value: string;
    label: string;
    origem: string;
  }[];
}

interface HierarquiaUsuarioItem {
  usernameDomSGE: string;
  pessoa_domsge: string;
  nome: string;
}

interface StatusOption {
  value: string;
  label: string;
}

export default function AtendimentoBpm(): JSX.Element {
  const [atendimentos, setAtendimentos] = useState<AtendimentoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [primeiraBuscaRealizada, setPrimeiraBuscaRealizada] = useState(false);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState('');
  const [hierarquiaUsuario, setHierarquiaUsuario] = useState<
    HierarquiaUsuarioItem[]
  >([]);
  const [acessoLimitadoBpm, setAcessoLimitadoBpm] = useState(false);
  const [listaResponsavel, setListaResponsavel] = useState<
    {
      ORIGEM: string;
      RESPONSAVEL: string;
    }[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);

  const getFormattedDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Inside your component, replace the current state declarations with:
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);
  // Filtros
  const [dataInicio, setDataInicio] = useState(getFormattedDate(thirtyDaysAgo));
  const [dataFim, setDataFim] = useState(getFormattedDate(today));
  const [solicitante, setSolicitante] = useState('');
  // const [statusSelecionado, setStatusSelecionado] = useState<StatusOption[]>(
  //   [],
  // );
  const [statusSelecionado, setStatusSelecionado] = useState<StatusOption[]>([
    { value: 'Em Fila', label: 'Em Fila' },
    { value: 'Em Andamento', label: 'Em Andamento' },
  ]);
  const [responsavel, setResponsavel] = useState('');

  // Status valores
  const statusOptions: StatusOption[] = [
    { value: 'Em Fila', label: 'Em Fila' },
    { value: 'Em Andamento', label: 'Em Andamento' },
    { value: 'Encerrado', label: 'Encerrado' },
  ];

  // Virtualized list cache
  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 150,
      minHeight: 100,
    }),
  );

  async function getAcessoLimitadoBpm() {
    const response = await api.get('/acesso-programa/acesso-limitado', {
      params: {
        programaId: 120,
      },
    });

    const retorno = response.data;

    setAcessoLimitadoBpm(retorno);
  }

  const buscaHierarquiaUsuario = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/integracao/atendimento-bpm/getHierarquiaUsuario`,
      );

      const array = response.data as any[];

      const unique = Array.from(
        new Map(array.map((obj: any) => [JSON.stringify(obj), obj])).values(),
      );

      setHierarquiaUsuario(unique);
    } finally {
      setLoading(false);
    }
  };

  const buscaResponsaveis = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/integracao/atendimento-bpm/getResponsaveis`,
      );
      setListaResponsavel(response.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAcessoLimitadoBpm();
    buscaHierarquiaUsuario();
    buscaResponsaveis();
  }, []);

  const buscarAtendimentos = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      if (dataInicio) {
        const [year, month, day] = dataInicio.split('-');
        const formattedDate = `${day}-${month}-${year}`;
        params.append('dataInicio', formattedDate);
      }
      if (dataFim) {
        const [year, month, day] = dataFim.split('-');
        const formattedDate = `${day}-${month}-${year}`;
        params.append('dataFim', formattedDate);
      }
      if (solicitante) params.append('solicitante', solicitante);
      if (statusSelecionado.length > 0) {
        params.append('status', statusSelecionado.map(s => s.value).join(','));
      }
      if (responsavel) params.append('responsavel', responsavel);

      const response = await api.get(
        `/integracao/atendimento-bpm?${params.toString()}`,
      );
      setAtendimentos(response.data);
      setPrimeiraBuscaRealizada(true);
    } catch (error) {
      console.error('Erro ao buscar atendimentos:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Ocorreu um erro ao buscar os atendimentos',
      });
    } finally {
      setLoading(false);
    }
  };

  const limparFiltros = () => {
    setDataInicio('');
    setDataFim('');
    setAtendimentos([]);
    setStatusSelecionado([]);
    setSolicitante('');
    setResponsavel('');
    setPrimeiraBuscaRealizada(false);
    // setUsuario('');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const toggleModal = (solicitacao?: string) => {
    setModalOpen(!modalOpen);
    if (solicitacao) {
      setSelectedSolicitacao(solicitacao);
    } else {
      setSelectedSolicitacao('');
    }
  };

  const renderRow = ({ index, key, style, parent }: any) => {
    const item = atendimentos[index];

    return (
      <CellMeasurer
        cache={cache.current}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        <div style={style} className="d-flex w-100">
          <div className="flex-grow-1 d-flex">
            {/* ID / Data Criação */}
            <div
              className="p-2 d-flex align-items-center justify-content-center"
              style={{ flex: 1 }}
            >
              <div className="d-flex flex-column align-items-center justify-content-center">
                <div className="small">{item.SEQ_FLUXO_REAL}</div>
                <div className="small">{formatDate(item.DATA_CRIACAO)}</div>
              </div>
            </div>

            {/* Solicitante */}
            <div
              className="p-2 d-flex align-items-center justify-content-center"
              style={{ flex: 3 }}
            >
              <div className="small">
                {item.SOLICITANTE === 'Sistema'
                  ? item.SOLICITANTE
                  : item.NOME_SOLICITANTE}
              </div>
            </div>

            {/* Status */}
            <div
              className="p-2 d-flex align-items-center justify-content-center"
              style={{ flex: 1 }}
            >
              <div className="small">{item.STATUS || '-'}</div>
            </div>

            {/* Responsável */}
            <div
              className="p-2 d-flex align-items-center justify-content-center"
              style={{ flex: 3 }}
            >
              <div className="small">{item.RESPONSAVEL || '-'}</div>
            </div>

            {/* Proposta/Configuração */}
            <div className="p-2  d-flex align-items-center" style={{ flex: 8 }}>
              {item.PROPOSTA ? (
                <div className="d-flex flex-wrap w-100" style={{ gap: '8px' }}>
                  <div
                    style={{ flex: 1 }}
                    className="d-flex flex-column justify-content-center"
                  >
                    <div className="small">
                      <strong>Configuração {item.CONFIGURACAO.id}</strong>
                    </div>
                    <div className="small">
                      <strong>Proposta {item.PROPOSTA.id}</strong>
                    </div>
                    <div className="small">
                      Versão {item.PROPOSTA.versao} | Gnf:{' '}
                      {item.CONFIGURACAO?.codigoOportunidadeDomsge}
                    </div>
                  </div>
                  <div
                    style={{ flex: 1 }}
                    className="d-flex flex-column justify-content-center"
                  >
                    <div className="small">
                      <strong>Localização:</strong>{' '}
                      {item.PROPOSTA?.proposta?.PropostaLocalObra?.municipio ||
                        'Não informada'}{' '}
                      /{' '}
                      {item.PROPOSTA?.proposta?.PropostaLocalObra?.uf ||
                        'Não informada'}
                    </div>
                    <div className="small">
                      <strong>Usuário:</strong>{' '}
                      {item.PROPOSTA?.proposta?.usuario?.nome ||
                        'Não informado'}
                    </div>
                    {item.HIERARQUIA && item.HIERARQUIA.pessoaCargoDesc && (
                      <div>
                        <small>
                          <strong>Função:</strong>{' '}
                          {item.HIERARQUIA.pessoaCargoDesc}
                        </small>
                      </div>
                    )}
                  </div>
                  {item.HIERARQUIA && (
                    <div
                      style={{ flex: 1 }}
                      className="d-flex flex-column justify-content-center"
                    >
                      {item.HIERARQUIA.pessoaRazao && (
                        <small>
                          <strong>Empresa:</strong>{' '}
                          {item.HIERARQUIA.pessoaRazao}
                        </small>
                      )}
                      {item.HIERARQUIA.pessoaSupRazao && (
                        <small>
                          <strong>Superior:</strong>{' '}
                          {item.HIERARQUIA.pessoaSupRazao} |{' '}
                          {item.HIERARQUIA.pessoaSupCargoDesc}
                        </small>
                      )}
                    </div>
                  )}
                </div>
              ) : item.CONFIGURACAO ? (
                <div
                  className="d-flex flex-wrap w-100 align-items-center"
                  style={{ flex: 1 }}
                >
                  <div
                    style={{ flex: 1 }}
                    className="d-flex flex-column justify-content-center"
                  >
                    <div className="small">
                      <strong>Configuração {item.CONFIGURACAO.id}</strong>
                    </div>
                    <div className="small">
                      Gnf: {item.CONFIGURACAO.codigoOportunidadeDomsge}
                    </div>
                  </div>

                  <div
                    style={{ flex: 1 }}
                    className="d-flex flex-column justify-content-center"
                  >
                    <div className="small">
                      <strong>Código:</strong>{' '}
                      {item.CONFIGURACAO.pessoa?.codigoDomsge ||
                        'Não informado'}
                    </div>
                    <div className="small">
                      <strong>Razão Social:</strong>{' '}
                      {item.CONFIGURACAO.pessoa?.razaoSocial || 'Não informada'}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center w-100 d-flex justify-content-center align-items-center">
                  -
                </div>
              )}
            </div>

            {/* Detalhes */}
            <div
              className="p- d-flex float-right justify-content-center align-items-center"
              style={{ flex: 2 }}
            >
              <div className="d-flex float-right justify-content-center align-items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleModal(item.SOLICITACAO)}
                  className="btn btn-sm btn-outline-info"
                  title="Detalhes"
                >
                  <FaEye />
                </button>
                <a
                  href={item.URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary"
                  title="Abrir Fluxo"
                >
                  <FaExternalLinkAlt />
                </a>
              </div>
            </div>
          </div>
        </div>
      </CellMeasurer>
    );
  };

  return (
    <>
      <UserSmallHeader />
      <Container fluid className="mt-4 px-0 mx-0" style={{ maxWidth: '100%' }}>
        <Card className="mx-2">
          <CardHeader>
            <strong className="text-muted">Atendimentos BPM</strong>
          </CardHeader>

          {/* Filtros */}
          <Card className="shadow pt-3 pb-2 px-2 mt-1 mb-0 mx-1">
            <Row>
              {/* Left Column - Data Inicio, Data Fim, Solicitante, Responsavel */}
              <Col sm="6">
                <Row>
                  {/* Data início */}
                  <Col sm="6" className="mb-2">
                    <TextField
                      label="Data Início"
                      type="date"
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={dataInicio}
                      onChange={e => setDataInicio(e.target.value)}
                    />
                  </Col>
                  {/* Data Final */}
                  <Col sm="6" className="mb-2">
                    <TextField
                      label="Data Final"
                      type="date"
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={dataFim}
                      onChange={e => setDataFim(e.target.value)}
                    />
                  </Col>
                </Row>
                <Row>
                  {/* Solicitante */}
                  <Col sm="6" className="mb-2">
                    <Select
                      options={hierarquiaUsuario
                        .filter(user => user?.usernameDomSGE)
                        .map(user => ({
                          value: user.usernameDomSGE,
                          label: `${user.pessoa_domsge} - ${user.nome}`,
                          name: user.nome,
                        }))
                        .sort((a, b) =>
                          (a.name || '').localeCompare(b.name || ''),
                        )}
                      placeholder="Selecione o solicitante"
                      value={
                        solicitante
                          ? {
                              value: solicitante,
                              label: hierarquiaUsuario.find(
                                user => user.usernameDomSGE === solicitante,
                              )?.nome,
                            }
                          : null
                      }
                      onChange={selected =>
                        setSolicitante(selected?.value || '')
                      }
                      isClearable
                      styles={{
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        menu: provided => ({
                          ...provided,
                          backgroundColor: '#ffffff',
                          zIndex: 9999,
                        }),
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? '#f0f0f0'
                            : 'transparent',
                          color: state.isSelected ? '#000000' : '#333',
                        }),
                      }}
                    />
                  </Col>
                  {/* Responsável */}
                  {!acessoLimitadoBpm && (
                    <Col sm="6" className="mb-2">
                      <Select
                        options={listaResponsavel
                          .filter(item => item?.RESPONSAVEL)
                          .reduce((groups: GroupedOption[], item) => {
                            const group = groups.find(
                              g => g.label === item.ORIGEM,
                            );
                            if (group) {
                              group.options.push({
                                value: item.RESPONSAVEL,
                                label: item.RESPONSAVEL,
                                origem: item.ORIGEM,
                              });
                            } else {
                              groups.push({
                                label: item.ORIGEM,
                                options: [
                                  {
                                    value: item.RESPONSAVEL,
                                    label: item.RESPONSAVEL,
                                    origem: item.ORIGEM,
                                  },
                                ],
                              });
                            }
                            return groups;
                          }, [] as GroupedOption[])
                          .map(group => ({
                            ...group,
                            label:
                              group.label === 'USUARIO' ? 'Usuários' : 'Grupos',
                            options: group.options.sort((a, b) =>
                              a.label.localeCompare(b.label, 'pt-BR', {
                                sensitivity: 'base',
                              }),
                            ),
                          }))
                          // Optional: Sort groups if needed
                          .sort((a, b) => a.label.localeCompare(b.label))}
                        placeholder="Selecione o Responsável"
                        value={
                          responsavel
                            ? {
                                value: responsavel,
                                label: responsavel,
                                origem: listaResponsavel.find(
                                  item => item.RESPONSAVEL === responsavel,
                                )?.ORIGEM,
                              }
                            : null
                        }
                        onChange={selected =>
                          setResponsavel(selected?.value || '')
                        }
                        isClearable
                        styles={{
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          menu: provided => ({
                            ...provided,
                            backgroundColor: '#ffffff',
                            zIndex: 9999,
                          }),
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? '#f0f0f0'
                              : 'transparent',
                            color: state.isSelected ? '#000000' : '#333',
                          }),
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          groupHeading: provided => ({
                            ...provided,
                            fontWeight: 'bold',
                            backgroundColor: '#f8f9fa',
                            padding: '8px 12px',
                          }),
                        }}
                      />
                    </Col>
                  )}
                </Row>
              </Col>

              {/* Right Column - Status */}
              <Col sm="4" className="mb-2">
                <Select
                  isMulti
                  options={statusOptions}
                  placeholder="Selecione os status"
                  value={statusSelecionado}
                  onChange={selected =>
                    setStatusSelecionado(selected as StatusOption[])
                  }
                  styles={{
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    menu: provided => ({
                      ...provided,
                      backgroundColor: '#ffffff',
                      zIndex: 9999,
                    }),
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected
                        ? '#f0f0f0'
                        : 'transparent',
                      color: state.isSelected ? '#000000' : '#333',
                    }),
                  }}
                />
              </Col>

              <Col sm="2" className="mb-2">
                <Button
                  color="primary"
                  outline
                  className="me-1"
                  onClick={limparFiltros}
                >
                  <FaEraser className="me-1" />
                </Button>
                <Button
                  color="primary"
                  onClick={buscarAtendimentos}
                  disabled={loading}
                >
                  <FaSearch className="me-1" />
                </Button>
              </Col>
            </Row>
          </Card>

          {loading ? (
            <Row>
              <Col className="text-center py-4">
                <CircularProgress />
              </Col>
            </Row>
          ) : atendimentos.length > 0 ? (
            <div className="px-2" style={{ overflowX: 'auto' }}>
              {/* Table Header */}
              <div
                className="d-flex w-100"
                style={{ borderBottom: '2px solid #dee2e6' }}
              >
                <div
                  className="p-2 small thead-light text-center fw-bold d-flex align-items-center justify-content-center"
                  style={{ flex: 1 }}
                >
                  <span className="">
                    <strong>ID / Data Criação</strong>
                  </span>
                </div>
                <div
                  className="p-2 small thead-light text-center fw-bold d-flex align-items-center justify-content-center"
                  style={{ flex: 3 }}
                >
                  <span className="">
                    <strong>Solicitante</strong>
                  </span>
                </div>
                <div
                  className="p-2 small thead-light text-center fw-bold d-flex align-items-center justify-content-center"
                  style={{ flex: 1 }}
                >
                  <span className="">
                    <strong>Status</strong>
                  </span>
                </div>
                <div
                  className="p-2 small thead-light text-center fw-bold d-flex align-items-center justify-content-center"
                  style={{ flex: 3 }}
                >
                  <span className="">
                    <strong>Responsável</strong>
                  </span>
                </div>
                <div
                  className="p-2 small thead-light text-center fw-bold d-flex align-items-center justify-content-center"
                  style={{ flex: 8 }}
                >
                  <span className="">
                    <strong>Proposta/Configuração</strong>
                  </span>
                </div>
                <div
                  className="p-2 small thead-light text-center fw-bold d-flex align-items-center justify-content-center"
                  style={{ flex: 2 }}
                >
                  <span className="" />
                </div>
              </div>

              {/* Table Body */}
              <div style={{ height: 'calc(100vh - 320px)' }}>
                <AutoSizer>
                  {({ width, height }) => (
                    <List
                      width={width}
                      height={height}
                      deferredMeasurementCache={cache.current}
                      rowHeight={cache.current.rowHeight}
                      rowRenderer={renderRow}
                      rowCount={atendimentos.length}
                      overscanRowCount={10}
                    />
                  )}
                </AutoSizer>
              </div>
            </div>
          ) : primeiraBuscaRealizada ? (
            <Row>
              <Col className="text-center py-4">
                {atendimentos.length === 0 && !loading
                  ? 'Nenhum atendimento encontrado. Ajuste os filtros e clique em Buscar.'
                  : null}
              </Col>
            </Row>
          ) : (
            <Row>
              <Col className="text-center py-4">
                {atendimentos.length === 0 && !loading
                  ? 'Selecione os filtros desejados e clique em Buscar para visualizar os atendimentos.'
                  : null}
              </Col>
            </Row>
          )}
        </Card>
      </Container>

      {/* Solicitação Modal */}
      <Modal isOpen={modalOpen} toggle={() => toggleModal()} size="lg">
        <ModalHeader toggle={() => toggleModal()}>
          Detalhes da Solicitação
        </ModalHeader>
        <ModalBody>
          <div dangerouslySetInnerHTML={{ __html: selectedSolicitacao }} />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => toggleModal()}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
